import React from 'react';
import { AlertTriangle, ArrowRight, Play } from 'lucide-react';
import { usePaywallCheck } from '@/hooks/usePaywallCheck';
import { usePaywallStore } from '@/stores/paywallStore';
import type { PaywallVariant } from '@/stores/paywallStore';

/**
 * Card shown at the top of the Credits page for all paywall scenarios.
 * Simpler than the dashboard banner — focused on immediate action.
 *
 * Handles all 5 scenarios per the Touchpoint Matrix.
 */
export function CreditsPaywallCard() {
  const { needsUpgrade, needsTrial, isCancelledTrial, isExhaustedTrial, isExhaustedPaid, isExhausted, balance, currentPeriodEnd } = usePaywallCheck();
  const openPaywall = usePaywallStore((s) => s.openPaywall);

  const isAnyPaywalled = needsTrial || isCancelledTrial || isExhaustedTrial || isExhaustedPaid || needsUpgrade;
  if (!isAnyPaywalled) return null;

  let formattedDate = 'soon';
  if (currentPeriodEnd) {
    const endDate = new Date(currentPeriodEnd);
    const diffDays = Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    const dateStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    formattedDate = diffDays <= 0 ? 'today' : diffDays === 1 ? 'tomorrow' : diffDays <= 14 ? `in ${diffDays} days` : dateStr;
  }

  let message: string;
  let ctaText: string;
  let variant: PaywallVariant;
  let iconColor: string;
  let iconBgFrom: string;
  let iconBgTo: string;
  let iconBorder: string;

  if (needsTrial) {
    // Scenario A
    message = 'Start a trial to get credits';
    ctaText = 'Start Free Trial';
    variant = 'trial';
    iconColor = '#c8d45a';
    iconBgFrom = 'rgba(200,212,90,0.15)';
    iconBgTo = 'rgba(168,180,74,0.15)';
    iconBorder = 'rgba(200,212,90,0.2)';
  } else if (isCancelledTrial) {
    // Scenario B
    message = 'Your trial was cancelled. Credits have been removed.';
    ctaText = 'View Plans';
    variant = 'cancelled_trial';
    iconColor = '#fb923c';
    iconBgFrom = 'rgba(251,146,60,0.15)';
    iconBgTo = 'rgba(239,68,68,0.15)';
    iconBorder = 'rgba(251,146,60,0.2)';
  } else if (isExhaustedTrial) {
    // Scenario C
    message = `You've used all your trial credits. Plan starts ${formattedDate}.`;
    ctaText = 'Start Plan Now';
    variant = 'exhausted_trial';
    iconColor = '#fbbf24';
    iconBgFrom = 'rgba(251,191,36,0.15)';
    iconBgTo = 'rgba(245,158,11,0.15)';
    iconBorder = 'rgba(251,191,36,0.2)';
  } else if (isExhaustedPaid) {
    // Scenario D
    message = `Your credits have run out. Credits refresh ${formattedDate}.`;
    ctaText = 'View Plans';
    variant = 'exhausted';
    iconColor = '#fb923c';
    iconBgFrom = 'rgba(251,146,60,0.15)';
    iconBgTo = 'rgba(239,68,68,0.15)';
    iconBorder = 'rgba(251,146,60,0.2)';
  } else {
    // Scenario E
    message = 'Your credits have run out.';
    ctaText = 'View Plans';
    variant = 'upgrade';
    iconColor = '#fb923c';
    iconBgFrom = 'rgba(251,146,60,0.15)';
    iconBgTo = 'rgba(239,68,68,0.15)';
    iconBorder = 'rgba(251,146,60,0.2)';
  }

  return (
    <div
      className="rounded-xl overflow-hidden mb-6"
      style={{
        background: 'linear-gradient(135deg, rgba(29,41,57,0.95) 0%, rgba(45,53,70,0.95) 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <div className="p-6 flex flex-col sm:flex-row items-center gap-5">
        {/* Icon */}
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
          style={{
            background: `linear-gradient(135deg, ${iconBgFrom} 0%, ${iconBgTo} 100%)`,
            border: `1px solid ${iconBorder}`,
          }}
        >
          {needsTrial ? (
            <Play className="w-6 h-6" style={{ color: iconColor }} />
          ) : (
            <AlertTriangle className="w-6 h-6" style={{ color: iconColor }} />
          )}
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0 text-center sm:text-left">
          <h3 className="text-base font-semibold text-white mb-0.5">{message}</h3>
          {!needsTrial && (
            <p className="text-sm text-white/50">
              Balance: <span style={{ color: iconColor }} className="font-medium">{Math.max(0, balance).toLocaleString()} credits</span>
            </p>
          )}
        </div>

        {/* CTA */}
        <button
          onClick={() => openPaywall('credits', variant)}
          className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
          style={{
            background: 'linear-gradient(135deg, #c8d45a 0%, #a8b44a 100%)',
            color: '#1d2939',
            boxShadow: '0 4px 16px rgba(200,212,90,0.2)',
          }}
        >
          {ctaText}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Gradient accent line */}
      <div className="h-0.5 bg-gradient-to-r from-transparent via-accent-400/30 to-transparent" />
    </div>
  );
}
